import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, TextField, Button, Box, MenuItem, FormHelperText } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import subscriptionApi from '../../../services/subscribe';
import authApi from '../../../services/auth';
import { showLoader, hideLoader } from '../../../redux/loader/actions';
import { useDispatch, useSelector } from 'react-redux';
import { getDecryptedCookie } from '../../../utils';
import { startOfDay, format } from 'date-fns';
import moment from 'moment';
function Sidebar({ searchParams, setSearchParams, paginationModelSub, setPaginationModelSub }) {
    const organizationItems = useSelector((state) => state.organizationReducer.items);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userId = getDecryptedCookie('uuid');
    const [subscriptionData, setSubscriptionData] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [productDetails, setProductDetails] = useState([]);
    const [fromDateError, setFromDateError] = useState('');
    const [toDateError, setToDateError] = useState('');

    const [status, setStatus] = useState('');
    const [filterBy, setFilterBy] = useState('');
    const validateUserToken = () => {
        dispatch(showLoader());
        const onSuccess = (res) => {
            if (res?.data?.status === true) {
                getSubscriptionData(res?.data?.uuid);
            }
        };
        const onFailure = (err) => {};
        authApi.validateToken().then(onSuccess, onFailure);
    };

    useEffect(() => {
        validateUserToken();
    }, [organizationItems]);

    const getSubscriptionData = useCallback(
        (id) => {
            const onSuccess = (res) => {
                if (res?.data?.status) {
                    setSubscriptionData(res?.data?.data?.data);
                }
            };
            const onFailure = (err) => {
                console.error(err?.response?.data?.msg);
            };
            subscriptionApi?.getAllSubscriptionByFilterId(id).then(onSuccess, onFailure);
        },
        [dispatch]
    );

    useEffect(() => {
        if (subscriptionData?.length) {
            setProductDetails(subscriptionData?.map((data) => data?.productPlanDetails?.product));
        }
    }, [subscriptionData, organizationItems]);

    const handleProductChange = (event) => {
        const selectedProductUuid = event.target.value;
        setSelectedProducts(selectedProductUuid);
    };

    const handleSearch = () => {
        setFromDateError('');
        setToDateError('');
        if (filterBy) {
            if (!fromDate) {
                setFromDateError('From Date is required.');
                return;
            }
        }
        if (!fromDateError && !toDateError) {
            const formattedFromDate = fromDate
                ? moment.utc(fromDate).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss[Z]')
                : null;

            const formattedToDate = toDate ? moment.utc(toDate).endOf('day').format('YYYY-MM-DD[T]HH:mm:ss[Z]') : null;

            setSearchParams({
                products: selectedProducts,
                fromDate: formattedFromDate,
                toDate: formattedToDate,
                status,
                filterBy
            });
            setPaginationModelSub({
                page: 0,
                pageSize: 10
            });
        }
    };

    const handleReset = () => {
        setSelectedProducts([]);
        setFromDate(null);
        setToDate(null);
        setStatus('');
        setFilterBy('');
        setFromDateError('');
        setToDateError('');

        setSearchParams({
            products: '',
            fromDate: '',
            toDate: '',
            status: '',
            filterBy: ''
        });
    };

    const handleFromDateChange = (newValue) => {
        if (newValue) {
            const normalizedFromDate = startOfDay(newValue);
            setFromDate(normalizedFromDate);

            if (normalizedFromDate && toDate && startOfDay(new Date(toDate)) < normalizedFromDate) {
                setFromDateError('From Date cannot be later than To Date.');
            } else {
                setFromDateError('');
            }
        }
    };

    const handleToDateChange = (newValue) => {
        if (newValue && new Date(newValue) < new Date(fromDate)) {
            setToDateError('To Date cannot be earlier than From Date.');
            return;
        }
        setToDate(newValue);
        setToDateError('');
    };

    return (
        <Card sx={{ p: 3, mb: 2 }} elevation={4}>
            <Grid container direction='column' spacing={2}>
                <Grid item>
                    <TextField
                        label='Product Name'
                        name='product'
                        fullWidth
                        select
                        value={selectedProducts}
                        onChange={handleProductChange}
                    >
                        <MenuItem value=''>Select</MenuItem>
                        {[
                            ...new Map(
                                productDetails
                                    ?.filter((product) => product?.name && product?.uuid)
                                    .map((product) => [product.name, product])
                            ).values()
                        ].map((product) => (
                            <MenuItem key={product?.uuid} value={product?.uuid}>
                                {product?.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item>
                    <TextField
                        label='Status'
                        name='status'
                        fullWidth
                        select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <MenuItem value=''>Select</MenuItem>
                        <MenuItem value='all'>All</MenuItem>
                        <MenuItem value='active'>Active</MenuItem>
                        <MenuItem value='cancelled'>Cancelled</MenuItem>
                        <MenuItem value='deleted'>Deleted</MenuItem>
                        <MenuItem value='expired'>Expired</MenuItem>
                    </TextField>
                </Grid>

                <Grid item>
                    <TextField
                        label='Filter By'
                        name='filterBy'
                        fullWidth
                        select
                        value={filterBy}
                        onChange={(e) => setFilterBy(e.target.value)}
                    >
                        <MenuItem value=''>Select</MenuItem>
                        <MenuItem value='start_date'>Purchased On</MenuItem>
                        <MenuItem value='expiry_date'>Expiry</MenuItem>
                        <MenuItem value='payment_date'>Payment On</MenuItem>
                    </TextField>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <TextField
                            id='outlined-basic'
                            value={fromDate ? moment(fromDate).format('YYYY-MM-DD') : ''}
                            onChange={(e) => setFromDate(e.target.value)}
                            label='From Date'
                            variant='outlined'
                            type='date'
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                        {fromDateError && <FormHelperText error>{fromDateError}</FormHelperText>}
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <TextField
                            id='outlined-basic'
                            value={toDate ? moment(toDate).format('YYYY-MM-DD') : ''}
                            onChange={(e) => setToDate(e.target.value)}
                            label='To Date'
                            variant='outlined'
                            type='date'
                            inputProps={{
                                min: fromDate ? moment(fromDate).format('YYYY-MM-DD') : ''
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                        {toDateError && <FormHelperText error>{toDateError}</FormHelperText>}
                    </Box>
                </Grid>
                <Grid item>
                    <Button
                        variant='contained'
                        fullWidth
                        sx={{
                            height: '100%',
                            background: 'linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)',
                            color: '#fff',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                            textTransform: 'uppercase',
                            fontWeight: 'bold',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)',
                                boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.3)'
                            }
                        }}
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                </Grid>

                <Grid item>
                    <Button variant='outlined' fullWidth color='primary' onClick={handleReset}>
                        Reset
                    </Button>
                </Grid>
            </Grid>
        </Card>
    );
}

export default Sidebar;
